import { useCallback, useEffect, useRef, useState } from 'react';

import useIsDarkTheme from '../../../core/hooks/useIsDarkTheme';

import type { CityBuilding, CityBundle, CityEvent } from './types';

export type AcerteiCityProps = {
  /** Dados a desenhar. Mande um objeto novo quando mudar; a cidade anima a diferença. */
  data: CityBundle | null;
  /** Caminho da página estática em `public/`. */
  src?: string;
  className?: string;
  /** Disparado quando o aluno abre um prédio da cidade. */
  onOpenBuilding?: (building: CityBuilding) => void;
  /** Disparado quando o painel do prédio é fechado. Par de `onOpenBuilding`. */
  onCloseBuilding?: () => void;
  /** Disparado pelo botão de ação do painel ("Responder questão", "Revisar flashcard"…). */
  onAction?: (building: CityBuilding, subjectId?: string) => void;
};

/**
 * Embute a cidade 3D (`public/cidade-acertei/index.html`) e a alimenta por
 * `postMessage`. Mesma origem sempre: o dev server e o build servem `public/`
 * na raiz, então o iframe nasce em `window.location.origin` e os dois lados
 * conferem `e.origin` antes de aceitar qualquer mensagem.
 *
 * Três coisas que não são óbvias:
 *
 * - **`?embed=1` é obrigatório.** Sem ele a cidade assume que está aberta
 *   sozinha, mantém o próprio botão "Dados" e tenta falar com a API por conta
 *   própria (lendo um token de um campo de formulário). Com ele, ela esconde
 *   esse botão, emite `acertei-city:ready` e passa a esperar os dados do host —
 *   que é o caminho que esta app usa, porque aqui o axios já tem o token e os
 *   interceptors.
 * - **O `postMessage` só sai depois do `ready`.** Mandar antes é mensagem
 *   perdida: o listener da cidade só existe depois que o script dela roda. Daí
 *   o estado `ready` e o efeito que (re)envia sempre que `data` muda.
 * - **O tema entra por dois caminhos, e os dois são necessários.** O `?theme=`
 *   na URL faz a PRIMEIRA pintura nascer certa (sem ele a cidade abre clara e
 *   pisca para escura quando a mensagem chega); o `acertei-city:theme` cobre a
 *   TROCA ao vivo, quando o usuário usa o botão de tema do produto — aí a URL
 *   já não muda, porque remontar o iframe reiniciaria a cena 3D.
 * - **Nada de three.js entra no bundle.** A cidade carrega three r128 e o
 *   OrbitControls de CDN, dentro do iframe. Por isso este componente não
 *   precisa de `React.lazy`: o custo dele aqui é uma tag `<iframe>`.
 */
export function AcerteiCity({
  data,
  src = '/cidade-acertei/index.html',
  className,
  onOpenBuilding,
  onCloseBuilding,
  onAction,
}: AcerteiCityProps) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const escuro = useIsDarkTheme();
  const tema = escuro ? 'dark' : 'light';

  // `src` é memorizado no primeiro render de propósito: trocar o `src` de um
  // iframe o recarrega, e isso reconstruiria a cidade inteira (three.js, texturas,
  // câmera) a cada troca de tema. O tema inicial vai na URL; as trocas seguintes
  // vão por mensagem.
  const srcInicial = useRef<string | null>(null);
  if (srcInicial.current === null) {
    srcInicial.current = `${src}?embed=1&theme=${tema}`;
  }

  // Os callbacks ficam numa ref para o listener de `message` ser registrado uma
  // única vez: dependê-los remontaria o listener a cada render do pai, e uma
  // mensagem que chegasse no intervalo entre o remove e o add seria perdida.
  const handlers = useRef({ onOpenBuilding, onCloseBuilding, onAction });
  handlers.current = { onOpenBuilding, onCloseBuilding, onAction };

  const send = useCallback((payload: CityBundle) => {
    frame.current?.contentWindow?.postMessage(
      { type: 'acertei-city:data', payload },
      window.location.origin,
    );
  }, []);

  useEffect(() => {
    const onMessage = (e: MessageEvent<CityEvent>) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return;
      const m = e.data;
      if (!m || typeof m !== 'object') return;
      if (m.type === 'acertei-city:ready') setReady(true);
      else if (m.type === 'acertei-city:open') handlers.current.onOpenBuilding?.(m.building);
      else if (m.type === 'acertei-city:close') handlers.current.onCloseBuilding?.();
      else if (m.type === 'acertei-city:action')
        handlers.current.onAction?.(m.building, m.subjectId);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (ready && data) send(data);
  }, [ready, data, send]);

  useEffect(() => {
    if (!ready) return;
    frame.current?.contentWindow?.postMessage(
      { type: 'acertei-city:theme', theme: tema },
      window.location.origin,
    );
  }, [ready, tema]);

  return (
    <iframe
      ref={frame}
      title="Cidade Acertei"
      src={srcInicial.current}
      allow="fullscreen"
      // Cor de espera até o primeiro frame do canvas, por tema: são os próprios
      // `--bg` da cidade (claro #e8eee9 / escuro #071b2b), copiados como
      // literais porque pertencem ao asset, não à paleta do produto — mesma
      // razão da placa hardcoded do `Wordmark`. Sem o par, o retângulo piscava
      // claro antes de a cena aparecer numa página escura.
      className={
        className ??
        `h-full w-full rounded-card border-0 ${escuro ? 'bg-[#071b2b]' : 'bg-[#e8eee9]'}`
      }
    />
  );
}

export default AcerteiCity;
